import React, { useCallback, useState, useRef } from 'react';
import { useParams } from 'react-router-dom';
import AddItemButton from '../../components/common/AddItemButton';
import FormInput from '../../components/resume/FormInput';
import DatePicker from '../../components/resume/DatePicker';
import FormTextarea from '../../components/resume/FormTextarea';
import { useResume } from '../../context/ResumeContext';
import AIWriteExperienceModal from '../../components/resume/AIWriteExperienceModal';
import useAutosave from '../../hooks/useAutosave';
import SaveStatusChip from '../../components/resume/editor/SaveStatusChip';
import FeedbackModal from '../../components/common/FeedbackModal';

const ExperienceItem = ({ experience, index, onUpdate, onDelete, onAiWrite, noJd }) => {
    const companyName = experience.company || 'THE COMPANY';

    return (
        <div className="bg-white/[0.04] border border-white/10 rounded-xl p-6 mb-6">
            <div className="flex justify-between items-start mb-6">
                <h2 className="text-xl font-bold">
                    {experience.role || `Experience ${index + 1}`}
                </h2>
                <button onClick={() => onDelete(experience.id)} className="bg-white/10 hover:bg-red-500/20 hover:text-red-300 text-gray-400 flex-shrink-0 ml-4 rounded p-1">
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                </button>
            </div>
            <form className="space-y-6">
                <FormInput label={`WHAT WAS YOUR ROLE AT ${companyName}? *`} name="role" value={experience.role} onChange={(e) => onUpdate(experience.id, { ...experience, role: e.target.value })} placeholder={'Data Scientist'} />
                <FormInput label="FOR WHICH COMPANY DID YOU WORK? *" name="company" value={experience.company} onChange={(e) => onUpdate(experience.id, { ...experience, company: e.target.value })} placeholder={'Google'} />

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                        <label className="block text-xs font-bold text-gray-400 uppercase mb-2">{`HOW LONG WERE YOU WITH ${companyName}?`}</label>
                        <div className="flex items-center gap-4">
                            <DatePicker
                                value={experience.startDate}
                                onSelect={(date) => onUpdate(experience.id, { ...experience, startDate: date })}
                            />
                            <DatePicker
                                value={experience.endDate}
                                onSelect={(date) => onUpdate(experience.id, { ...experience, endDate: date, isCurrent: false })}
                                startDate={experience.startDate}
                                showToggle={true}
                                isCurrent={experience.isCurrent}
                                onToggleCurrent={() => onUpdate(experience.id, { ...experience, isCurrent: !experience.isCurrent, endDate: !experience.isCurrent ? 'Present' : '' })}
                            />
                        </div>
                    </div>
                    <FormInput label={`WHERE WAS ${companyName} LOCATED?`} name="location" value={experience.location} onChange={(e) => onUpdate(experience.id, { ...experience, location: e.target.value })} placeholder={"Mountain View, CA"} />
                </div>

                <div>
                    <div className="flex justify-between items-center mb-2">
                        <label className="block text-xs font-bold text-gray-400 uppercase">{`WHAT DID YOU DO AT ${companyName}?`}</label>
                        <div className="flex flex-col items-end gap-1">
                            <button
                                type="button"
                                onClick={() => onAiWrite(experience.id)}
                                className="bg-cyan-500 hover:bg-cyan-400 text-black text-xs font-semibold py-1 px-3 rounded-md"
                            >
                                AI Write
                            </button>
                            {noJd && (
                                <span className="text-xs text-red-400">Add a job description on the Final Resume page first.</span>
                            )}
                        </div>
                    </div>
                    <FormTextarea
                        name="bullets"
                        value={experience.bullets}
                        onChange={(e) => onUpdate(experience.id, { ...experience, bullets: e.target.value })}
                        rows="8"
                    />
                </div>
            </form>
        </div>
    );
};

const Experience = () => {
    const { experiences, setExperiences, addExperience, jobDescription, aiAnalysis } = useResume();
    const { resumeId } = useParams();
    const [aiWriteModalOpen, setAiWriteModalOpen] = useState(false);
    const [noJdWarningExpId, setNoJdWarningExpId] = useState(null);
    const [modalInfo, setModalInfo] = useState({ isOpen: false, title: '', message: '', isError: false });
    const idMapRef = useRef(new Map());

    const updateExperience = useCallback((id, updatedData) => {
        setExperiences(currentExperiences =>
            currentExperiences.map(exp => (exp.id === id ? updatedData : exp))
        );
    }, [setExperiences]);

    const deleteExperience = (id) => {
        if (experiences.length > 1) {
            setExperiences(experiences.filter(exp => exp.id !== id));
        } else {
            setModalInfo({ isOpen: true, title: 'Notice', message: 'You must have at least one experience entry.', isError: false });
        }
    };

    const saveAllExperiences = async () => {
        if (!resumeId) throw new Error('no resume ID');
        let saveErrors = 0;

        const savePromises = experiences.map(exp => {
            if (exp.role || exp.company) {
                const effectiveId = idMapRef.current.get(exp.id) ?? exp.id;
                return fetch('https://renaisons.com/api/save_experience.php', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ ...exp, id: effectiveId, resume_id: resumeId }),
                })
                    .then(response => response.json())
                    .then(result => {
                        if (result.status === 'success' && result.experience_id && exp.id !== result.experience_id) {
                            idMapRef.current.set(exp.id, result.experience_id);
                            return { oldId: exp.id, newId: result.experience_id };
                        } else if (result.status !== 'success') {
                            console.error(`Error saving experience "${exp.role || 'New'}": ${result.message}`);
                            saveErrors++;
                        }
                        return null;
                    })
                    .catch(error => {
                        console.error(`Network error saving experience "${exp.role || 'New'}":`, error);
                        saveErrors++;
                        return null;
                    });
            }
            return Promise.resolve(null);
        });

        try {
            const results = await Promise.all(savePromises);
            const updates = results.filter(Boolean);
            if (updates.length > 0) {
                setExperiences(currentExperiences =>
                    currentExperiences.map(exp => {
                        const update = updates.find(u => u.oldId === exp.id);
                        return update ? { ...exp, id: update.newId } : exp;
                    })
                );
            }

            if (saveErrors > 0) {
                throw new Error('some entries failed');
            }
        } catch (error) {
            console.error('Failed to save experiences:', error);
            throw error;
        }
    };

    const validateExperiences = () => {
        const hasAnyData = experiences.some(exp =>
            exp.role || exp.company || exp.startDate || exp.endDate || exp.location || exp.bullets
        );
        if (!hasAnyData) return null;
        const hasInvalid = experiences.some(exp =>
            (exp.startDate || exp.endDate || exp.location || exp.bullets) && !exp.role && !exp.company
        );
        return hasInvalid ? false : true;
    };

    const { status, retry } = useAutosave({ sectionKey: 'experience', data: experiences, save: saveAllExperiences, validate: validateExperiences });

    const handleAiWrite = (id) => {
        if (!jobDescription || !jobDescription.trim()) {
            setNoJdWarningExpId(id);
            return;
        }
        setNoJdWarningExpId(null);
        setAiWriteModalOpen(true);
    };

    const handleAiInsert = (items) => {
        setExperiences(currentExperiences => {
            const updated = currentExperiences.map(exp => {
                const matching = items.filter(item => String(item.experienceId) === String(exp.id));
                if (matching.length === 0) return exp;
                let currentBullets = exp.bullets ? exp.bullets.trim() : '';
                matching.forEach(({ bullet }) => {
                    currentBullets = (!currentBullets || currentBullets === 'â€¢')
                        ? bullet
                        : `${currentBullets}\n${bullet}`;
                });
                return { ...exp, bullets: currentBullets };
            });
            return updated;
        });
    };

    return (
        <>
            {modalInfo.isOpen && (
                <FeedbackModal
                    title={modalInfo.title}
                    message={modalInfo.message}
                    isError={modalInfo.isError}
                    onClose={() => setModalInfo({ ...modalInfo, isOpen: false })}
                />
            )}
            {aiWriteModalOpen && (
                <AIWriteExperienceModal
                    jobDescription={jobDescription}
                    experiences={experiences}
                    aiAnalysis={aiAnalysis}
                    onInsert={handleAiInsert}
                    onClose={() => setAiWriteModalOpen(false)}
                />
            )}
            {experiences.map((exp, index) => (
                <ExperienceItem
                    key={exp.id}
                    experience={exp}
                    index={index}
                    onUpdate={updateExperience}
                    onDelete={deleteExperience}
                    onAiWrite={handleAiWrite}
                    noJd={noJdWarningExpId === exp.id}
                />
            ))}
            <div className="flex items-center justify-between mt-4">
                <AddItemButton onClick={addExperience}>ADD ANOTHER EXPERIENCE</AddItemButton>
                <SaveStatusChip status={status} onRetry={retry} />
            </div>
        </>
    );
};

export default Experience;
